import { amountToNumber, extractId } from '../../../util/shared/vue-global';

export const warDetailsElements = ['fund', 'department', 'account'];

export const dateForCriteria = ['fromRegisterDate', 'toRegisterDate', 'fromPaidCancelledDate', 'toPaidCancelledDate'];

export const getDefaultSumOfTrans = () => ({ isDisplay: false, totalAmt: 0.0, totalCount: 0 });

/*
  A fund flagged paymentNumberFromWarrant (Kay's remit funds) draws its payment number from the
  warrant's own number, not from the fund's payment sequence - so no "next payment" lookup applies
  and each warrant needs its own value. `fund` is a fund object or an id; an id is looked up in
  `fundList` (the screen's budgeted / cash funds for the fiscal year, loaded with the flag), and a
  fund object that is not in that list falls back to its own flag.
*/
export const isPaymentNumberFromWarrantFund = (fund, fundList = []) => {
  const listed = (fundList || []).find((f) => f._id === extractId(fund));
  return !!((listed || fund || {}).paymentNumberFromWarrant);
};

export const createWarTransactionGroup = (transArr = []) => {
  const groupedData = {};
  transArr.forEach((element) => {
    let keyObj = {
      fundId: element.details[0].fund._id,
      fund: element.details[0].fund.fund,
      fiscalYear: element.fiscalYear,
      warrant: element.warrant,
    };
    keyObj = JSON.stringify(keyObj);
    const stringKey = groupedData[keyObj];
    if (stringKey) {
      const updatedArr = stringKey;
      updatedArr.push(element);
      groupedData[keyObj] = updatedArr;
    } else {
      const newArr = [];
      newArr.push(element);
      groupedData[keyObj] = newArr;
    }
  });
  return groupedData;
};

export const upsertTransactionItem = async (item) => {
  const updatedItem = { ...item };
  delete updatedItem.hasFiles;
  if (updatedItem.paidCancelledDate === '' || updatedItem.status === 'OUTSTANDING') {
    updatedItem.paidCancelledDate = null;
  }
  if (updatedItem.details !== undefined && updatedItem.details.length > 0) {
    updatedItem.details = updatedItem.details.map((element) => {
      const amount = amountToNumber(element.amount);
      const detailItem = { ...element };
      if (detailItem.fund) {
        detailItem.fund = extractId(detailItem.fund);
      }
      if (detailItem.account) {
        detailItem.account = extractId(detailItem.account);
      }
      if (detailItem.department) {
        detailItem.department = extractId(detailItem.department);
      }
      return { ...detailItem, amount };
    });
  }
  return updatedItem;
};

export const programDetails = (program) => {
  const warTransactionDetails = {
    model: 'warTransaction',
    baseCrudKey: 'warTransaction',
    programName: 'Warrants & Appropriations',
    attachement: {
      bucketName: 'war',
      program: 'war',
      parentObjectType: 'warTransaction',
    },
  };
  const payrollTransactionDetails = {
    model: 'payrollTransaction',
    baseCrudKey: 'payrollTransaction',
    programName: 'Payroll Register',
    attachement: {
      bucketName: 'payroll',
      program: 'payroll',
      parentObjectType: 'payrollTransaction',
    },
  };

  /*
    This util capable to return same details for multiple keys as per requirenemnt
  */
  const programMapper = {
    // baseCrudKey
    warTransaction: warTransactionDetails,
    payrollTransaction: payrollTransactionDetails,
    // router
    war: warTransactionDetails,
    payroll: payrollTransactionDetails,
    // for profile mappers
    WAR: warTransactionDetails,
    PAYROLL: payrollTransactionDetails,
  };

  return programMapper[program];
};

export const programToModel = (program) => programDetails(program).model;

export const getProgramDetailsByParams = (self) => {
  const { program } = self.$route.params;
  if (program) {
    return [programDetails(program), program];
  }
  return [{}, ''];
};
/*
  "Enable Insufficient Funds Warning": the confirm text for what warNegativeBalances reported.
  `negatives` are { level: 'fund' | 'line', fund, department, account } - a fund, or a
  fund + department + account line the warrant would take below zero.
*/
export const negativeBalanceWarning = (negatives) => {
  const places = (negatives || []).map((n) => (n.level === 'line'
    ? `Fund ${n.fund || ''} / Dept ${n.department || ''} / Account ${n.account || ''}`
    : `Fund ${n.fund || ''}`));
  return 'Warning: This Warrant Transaction will result in a negative balance'
    + ` for ${places.join('; ')}. Do you wish to proceed?`;
};

/* FYI fundBalance & accountBalance values are consuming by backend, please update them carefully */
export const warStatsBalanceParams = () => ({
  fundBalance: 'fundBalance',
  accountBalance: 'accountBalance',
});

/*
  TKT-000812 - a cancelled warrant puts its money back into its own fiscal year automatically,
  and the Appropriations list shows that as a display-only line. The line is NOT a stored a/s/t
  record: it cannot be selected, edited or deleted, and nothing in the screen's totals reads it.
*/
export const toCancelledAddBackRow = (line) => ({
  _id: `cancelled-warrant-${line.warrantId}-${line.detailIndex}`,
  type: 'CANCELLED WARRANT',
  fiscalYear: line.fiscalYear,
  warrant: line.warrant,
  registerDate: line.paidCancelledDate,
  paidCancelledDate: line.paidCancelledDate,
  whomStr: line.description,
  amount: line.amount,
  details: line.details,
  // read by CRUDRow (no checkbox, no edit on click) and by v-data-table's select-all
  crudRowReadOnly: true,
  isSelectable: false,
});

const addBackSortValue = (row, column) => {
  if (column === 'registerDate') return new Date(row.registerDate).getTime();
  if (column === 'amount') return Number(row.amount);
  if (column === 'whomStr') return String(row.whomStr || '').toLowerCase();
  return undefined;
};

const compareValues = (a, b) => {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
};

/*
  Slots the add-back lines into the page the server returned, in the page's own sort order. The
  server sorted the real rows, so each line goes before the first row it sorts ahead of. A sort
  on a column the lines have no value for (fund, type...) leaves them at the end.
*/
export const mergeCancelledAddBacks = (rows, lines, sortBy) => {
  if (!Array.isArray(lines) || lines.length === 0) return rows;
  const [{ column, direction } = {}] = sortBy || [];
  const factor = direction === 'desc' ? -1 : 1;
  const merged = [...rows];
  lines.map(toCancelledAddBackRow).forEach((line) => {
    const lineValue = addBackSortValue(line, column);
    const at = lineValue === undefined ? -1 : merged.findIndex((row) => {
      const rowValue = addBackSortValue(row, column);
      return rowValue !== undefined && compareValues(lineValue, rowValue) * factor < 0;
    });
    if (at === -1) merged.push(line);
    else merged.splice(at, 0, line);
  });
  return merged;
};

export const extractClearingFundNumber = (clearingFundNumWithBank) => {
  const clearingFundNumOnly = clearingFundNumWithBank.map((item) => {
    const match = item.match(/^\s*(\d+)(?:-|\s|$)/);
    return match ? match[1] : null;
  }).filter(Boolean); // Boolean is to remove null values in array.
  return clearingFundNumOnly;
};
